import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';

// components and helpers
import utils from '../../utils/utils';
import Loader from '../Shared/Loader';
import * as confirmActions from '../Confirmation/ConfirmationActions';

// mui
import IconButton from '@mui/material/IconButton';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

interface PendingInvitesProps {
    handleFetchMore: any;
    setOpenPendingInvites: any;
}

const PendingInvites: React.FC<PendingInvitesProps> = ({
    handleFetchMore,
    setOpenPendingInvites,
}) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const [hasMore, setHasMore] = useState<boolean>(true);
    const pendingInvitesList = useSelector(
        (state: any) => state.security.pendingInvitesList
    );
    const pendingInvitesListLoading = useSelector(
        (state: any) => state.security.pendingInvitesListLoading
    );

    useEffect(() => {
        if (
            pendingInvitesList &&
            pendingInvitesList.items &&
            pendingInvitesList.items.length === pendingInvitesList.total
        ) {
            setHasMore(false);
        }
    }, [pendingInvitesList]);

    const handleInvitation = (
        inviteId: string,
        status: string,
        workspaceId: string
    ) => {
        switch (status) {
            case 'ACCEPTED':
                dispatch(
                    confirmActions.showConfirmation(
                        'Accept Invitation',
                        'Are you sure you want to accept the invitation for this workspace?',
                        'acceptInvitation',
                        inviteId,
                        status,
                        workspaceId
                    )
                );
                break;
            case 'REJECTED':
                dispatch(
                    confirmActions.showConfirmation(
                        'Reject Invitation',
                        'Are you sure you want to reject the invitation for this workspace?',
                        'rejectInvitation',
                        inviteId,
                        status
                    )
                );
                break;
            default:
                break;
        }
    };

    const createCard = (item) => {
        return (
            <Grid
                container
                direction={'row'}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ p: 1, backgroundColor: '#6D50A710', borderRadius: 1 }}
            >
                <Grid item xs={9}>
                    <Typography variant={'caption'}>
                        <b>Workspace</b>: {item.workspace.name || '---'}
                    </Typography>
                    <br />
                    <Typography variant={'caption'}>
                        <b>Invite Role</b>:{' '}
                        {utils.toTitleText(item.role.toLowerCase()) || '---'}
                    </Typography>
                    <br />
                    <Typography variant={'caption'}>
                        <b>Invite Date</b>:{' '}
                        {utils.formatDate(item.lastUpdated || '---', 'L LTS')}
                    </Typography>
                    <br />
                    <Typography variant={'caption'}>
                        <b>Invited By</b>: {item.createdBy}
                    </Typography>
                </Grid>
                <Grid item xs={3}>
                    <Grid container>
                        <Grid item>
                            <IconButton
                                data-testid={'accept-invite'}
                                size={'small'}
                                onClick={() =>
                                    handleInvitation(
                                        item.id,
                                        'ACCEPTED',
                                        item.workspaceId
                                    )
                                }
                            >
                                <CheckCircleRoundedIcon
                                    style={{
                                        color: '#008F82',
                                        backgroundColor: '#fff',
                                        borderRadius: 50,
                                    }}
                                />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <IconButton
                                data-testid={'reject-invite'}
                                size={'small'}
                                onClick={() =>
                                    handleInvitation(
                                        item.id,
                                        'REJECTED',
                                        item.workspaceId
                                    )
                                }
                            >
                                <CancelRoundedIcon
                                    style={{
                                        color: '#D32F2F',
                                        backgroundColor: '#fff',
                                        borderRadius: 50,
                                    }}
                                />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const loadingComponent = (
        <div
            style={{
                position: 'absolute',
                zIndex: 110,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: `#fff80`,
            }}
        >
            <Loader size={60} color={'#60CEBD'} />
        </div>
    );
    return (
        <Paper sx={{ padding: 2, height: '100%' }} elevation={0}>
            <Grid
                container
                justifyContent={'space-between'}
                sx={{ mb: 2 }}
                alignItems={'center'}
            >
                <Grid item>
                    <Typography variant={'h6'}>
                        Pending Workspace Invitations
                    </Typography>
                </Grid>
                <Grid item>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenPendingInvites(false)}
                        size={'small'}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </Grid>
            </Grid>
            <div
                id="invitesList"
                style={{ position: 'relative', cursor: 'auto' }}
            >
                {pendingInvitesListLoading && loadingComponent}
                <InfiniteScroll
                    dataLength={
                        (pendingInvitesList &&
                            pendingInvitesList.items &&
                            pendingInvitesList.total) ||
                        0
                    }
                    next={handleFetchMore}
                    hasMore={hasMore}
                    loader={
                        <Grid
                            container
                            justifyContent={'center'}
                            style={{
                                marginTop: 5,
                                marginBottom: '5vh',
                            }}
                            direction={'column'}
                            alignItems={'center'}
                            alignContent={'center'}
                        >
                            <Grid item>
                                <Loader color={'#60CEBD'} size={50} />
                            </Grid>
                        </Grid>
                    }
                    height={'100%'}
                    endMessage={
                        <Typography
                            variant={'caption'}
                            color={theme.palette.text.secondary}
                            style={{ textAlign: 'center' }}
                        >
                            {pendingInvitesList &&
                            pendingInvitesList.items &&
                            pendingInvitesList.total
                                ? ``
                                : null}
                        </Typography>
                    }
                >
                    {pendingInvitesList && pendingInvitesList.items.length ? (
                        pendingInvitesList.items.map((item, i) => {
                            return (
                                <div
                                    style={{ marginBottom: 10 }}
                                    key={`pendingInvite-${i}`}
                                >
                                    {createCard(item)}
                                </div>
                            );
                        })
                    ) : (
                        <Typography
                            variant={'caption'}
                            color={theme.palette.text.secondary}
                        >
                            No pending invitations
                        </Typography>
                    )}
                </InfiniteScroll>
            </div>
        </Paper>
    );
};

export default PendingInvites;
